import {FabricObject, FabricText, Polyline} from 'fabric';
import {
    FloorPlanItem,
    ShapeElement,
    ShapeType,
    TableCanvas,
    TableShape,
    TextCanvas,
    WallCanvas,
} from '../types';
import {FABRIC_DEFAULTS} from './fabric-defaults';
import {
    MIN_SHAPE_HEIGHT,
    MIN_SHAPE_WIDTH,
    MIN_TEXT_FONT_SIZE,
    RESIZE_SCALE_EPSILON,
    TABLE_MIN_SIZE,
} from './constants';

export interface ScaledDimensions {
    width: number;
    height: number;
}

// Fabric keeps resize as temporary scale until we bake it into stored geometry.
const getScaleMagnitude = (scale?: number): number => {
    const value = Math.abs(scale ?? 1);
    return value > 0 ? value : 1;
};

const clampPositiveScaleMagnitude = (scale: number | undefined, minScale: number): number =>
    Math.max(getScaleMagnitude(scale), minScale);

const clampDimension = (value: number, min: number): number => Math.max(min, value);

const getUniformScaleMagnitude = (scaleX?: number, scaleY?: number, minScale = 1): number =>
    Math.max(getScaleMagnitude(scaleX), getScaleMagnitude(scaleY), minScale);

const getTextScaleMagnitude = (scaleX?: number, scaleY?: number, minScale = 1): number => {
    const x = getScaleMagnitude(scaleX);
    const y = getScaleMagnitude(scaleY);
    const xDelta = Math.abs(x - 1);
    const yDelta = Math.abs(y - 1);

    const dominant = xDelta === 0 && yDelta === 0
        ? 1
        : (xDelta >= yDelta ? x : y);

    return Math.max(dominant, minScale);
};

export const isUniformShapeType = (shape: ShapeType): boolean =>
    shape === 'circle' || shape === 'hexagon' || shape === 'octagon';

const setScaleWithFixedAnchor = (
    obj: FabricObject,
    scaleX: number,
    scaleY: number,
    originX?: FabricObject['originX'],
    originY?: FabricObject['originY']
) => {
    if (!originX || !originY) {
        obj.set({scaleX, scaleY});
        obj.setCoords();
        return;
    }

    const anchor = obj.translateToOriginPoint(obj.getRelativeCenterPoint(), originX, originY);
    obj.set({scaleX, scaleY});
    obj.setPositionByOrigin(anchor, originX, originY);
    obj.setCoords();
};

export const hasResidualScale = (scaleX?: number, scaleY?: number): boolean =>
    Math.abs((scaleX ?? 1) - 1) > RESIZE_SCALE_EPSILON || Math.abs((scaleY ?? 1) - 1) > RESIZE_SCALE_EPSILON;

// True when a Fabric object still carries temporary resize scale.
export const objectHasResidualScale = (obj: FabricObject): boolean =>
    hasResidualScale(obj.scaleX, obj.scaleY);

export const getTableMinDimensions = (_item: FloorPlanItem): ScaledDimensions => {
    return {
        width: TABLE_MIN_SIZE,
        height: TABLE_MIN_SIZE,
    };
};

export const getNormalizedTableDimensions = (
    canvas: TableCanvas,
    shape: TableShape,
    scaleX?: number,
    scaleY?: number
): ScaledDimensions => {
    const width = (canvas.width ?? FABRIC_DEFAULTS.table.width) * getScaleMagnitude(scaleX);
    const height = (canvas.height ?? FABRIC_DEFAULTS.table.height) * getScaleMagnitude(scaleY);
    const isCircle = shape === 'circle';
    const uniformSize = clampDimension(Math.max(width, height), TABLE_MIN_SIZE);
    return {
        width: isCircle ? uniformSize : clampDimension(width, TABLE_MIN_SIZE),
        height: isCircle ? uniformSize : clampDimension(height, TABLE_MIN_SIZE),
    };
};

export const getNormalizedShapeDimensions = (
    canvas: ShapeElement['canvas'],
    shape: ShapeType,
    scaleX?: number,
    scaleY?: number
): ScaledDimensions => {
    const width = clampDimension((canvas.width ?? FABRIC_DEFAULTS.shape.width) * getScaleMagnitude(scaleX), MIN_SHAPE_WIDTH);
    const height = clampDimension((canvas.height ?? FABRIC_DEFAULTS.shape.height) * getScaleMagnitude(scaleY), MIN_SHAPE_HEIGHT);

    if (isUniformShapeType(shape)) {
        const size = Math.max(width, height);
        return {width: size, height: size};
    }

    return {width, height};
};

export const getTextDisplayDimensions = (obj: FabricObject): ScaledDimensions => ({
    width: (obj.width ?? 0) * getScaleMagnitude(obj.scaleX),
    height: (obj.height ?? 0) * getScaleMagnitude(obj.scaleY),
});

export const getNormalizedTableCanvas = (
    canvas: TableCanvas,
    shape: TableShape,
    scaleX?: number,
    scaleY?: number
): TableCanvas => {
    const dimensions = getNormalizedTableDimensions(canvas, shape, scaleX, scaleY);
    return {
        ...canvas,
        width: dimensions.width,
        height: dimensions.height,
        scaleX: 1,
        scaleY: 1,
    };
};

export const getNormalizedShapeCanvas = (
    canvas: ShapeElement['canvas'],
    shape: ShapeType,
    scaleX?: number,
    scaleY?: number
): ShapeElement['canvas'] => {
    const dimensions = getNormalizedShapeDimensions(canvas, shape, scaleX, scaleY);
    return {
        ...canvas,
        width: dimensions.width,
        height: dimensions.height,
        scaleX: 1,
        scaleY: 1,
    };
};

export const getNormalizedTextCanvas = (
    canvas: TextCanvas,
    scaleX?: number,
    scaleY?: number
): TextCanvas => {
    const baseFontSize = canvas.fontSize ?? FABRIC_DEFAULTS.text.fontSize;
    const minScale = MIN_TEXT_FONT_SIZE / baseFontSize;
    const uniformScale = getTextScaleMagnitude(scaleX, scaleY, minScale);
    return {
        ...canvas,
        fontSize: clampDimension(Math.round(baseFontSize * uniformScale), MIN_TEXT_FONT_SIZE),
        scaleX: 1,
        scaleY: 1,
    };
};

export const getNormalizedWallPoints = (canvas: WallCanvas, scaleX?: number, scaleY?: number): WallCanvas['points'] => {
    const points = canvas.points ?? [];
    return points.map((point) => ({
        x: point.x * getScaleMagnitude(scaleX),
        y: point.y * getScaleMagnitude(scaleY),
    }));
};

// Clamp during drag so rebuild does not need a second corrective resize afterwards.
export const clampObjectScalingToMinimum = (
    obj: FabricObject,
    originX?: FabricObject['originX'],
    originY?: FabricObject['originY']
): boolean => {
    if (obj.floorPlanItem?.element?.kind === 'shape') {
        const canvas = obj.floorPlanItem.element.canvas;
        const shape = obj.floorPlanItem.element.shape;
        const baseWidth = canvas.width ?? FABRIC_DEFAULTS.shape.width;
        const baseHeight = canvas.height ?? FABRIC_DEFAULTS.shape.height;
        const nextScaleX = isUniformShapeType(shape)
            ? getUniformScaleMagnitude(obj.scaleX, obj.scaleY, Math.max(MIN_SHAPE_WIDTH / baseWidth, MIN_SHAPE_HEIGHT / baseHeight))
            : clampPositiveScaleMagnitude(obj.scaleX, MIN_SHAPE_WIDTH / baseWidth);
        const nextScaleY = isUniformShapeType(shape)
            ? nextScaleX
            : clampPositiveScaleMagnitude(obj.scaleY, MIN_SHAPE_HEIGHT / baseHeight);

        if (nextScaleX !== (obj.scaleX ?? 1) || nextScaleY !== (obj.scaleY ?? 1)) {
            setScaleWithFixedAnchor(obj, nextScaleX, nextScaleY, originX, originY);
            return true;
        }
        return false;
    }

    if (obj instanceof FabricText && obj.floorPlanItem?.element?.kind === 'text') {
        const canvas = obj.floorPlanItem.element.canvas as TextCanvas;
        const baseFontSize = canvas.fontSize ?? FABRIC_DEFAULTS.text.fontSize;
        const minScale = MIN_TEXT_FONT_SIZE / baseFontSize;
        const nextScaleX = getTextScaleMagnitude(obj.scaleX, obj.scaleY, minScale);
        const nextScaleY = nextScaleX;

        if (nextScaleX !== (obj.scaleX ?? 1) || nextScaleY !== (obj.scaleY ?? 1)) {
            setScaleWithFixedAnchor(obj, nextScaleX, nextScaleY, originX, originY);
            return true;
        }
    }

    return false;
};

// Rebuilds and saving work from normalized item data, not live Fabric scale.
export const buildNormalizedItemFromObject = (obj: FabricObject): FloorPlanItem | null => {
    const item = obj.floorPlanItem;
    if (!item) return null;

    if (item.table) {
        return {
            ...item,
            table: {
                ...item.table,
                canvas: getNormalizedTableCanvas(item.table.canvas, item.table.shape, obj.scaleX, obj.scaleY),
            },
        };
    }

    if (obj instanceof FabricText && item.element?.kind === 'text') {
        return {
            ...item,
            element: {
                ...item.element,
                canvas: getNormalizedTextCanvas(item.element.canvas as TextCanvas, obj.scaleX, obj.scaleY),
            },
        };
    }

    if (obj instanceof Polyline && item.element?.kind === 'wall') {
        return {
            ...item,
            element: {
                ...item.element,
                canvas: {
                    ...(item.element.canvas as WallCanvas),
                    points: getNormalizedWallPoints(item.element.canvas as WallCanvas, obj.scaleX, obj.scaleY),
                    scaleX: 1,
                    scaleY: 1,
                },
            },
        };
    }

    if (item.element?.kind === 'shape') {
        return {
            ...item,
            element: {
                ...item.element,
                canvas: getNormalizedShapeCanvas(item.element.canvas, item.element.shape, obj.scaleX, obj.scaleY),
            },
        };
    }

    return null;
};
